import React, { useCallback, useMemo } from 'react';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import strings from 'strings';

import { usePaginationStyles } from './Pagination.styled';
import { calculateVisiblePages } from './Pagination.utils';

const paginationCopies = strings.resumeSearch.pagination;

const PaginationComponent = ({
  currentPage = 1,
  totalPages = 1,
  totalItems = 0,
  itemsPerPage = 10,
  onPageChange,
  onItemsPerPageChange,
  itemsPerPageOptions = [5, 10, 25, 50]
}) => {
  const classes = usePaginationStyles();
  const startItem = totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const endItem = Math.min(currentPage * itemsPerPage, totalItems);

  // Memoize page change handler
  const handlePageChange = useCallback(
    newPage => {
      if (onPageChange && newPage >= 1 && newPage <= totalPages) {
        onPageChange(newPage);
      }
    },
    [onPageChange, totalPages]
  );

  const handleItemsPerPageChange = useCallback(
    event => {
      if (onItemsPerPageChange) {
        onItemsPerPageChange(event.target.value);
      }
    },
    [onItemsPerPageChange]
  );

  const visiblePages = useMemo(
    () => calculateVisiblePages(currentPage, totalPages),
    [currentPage, totalPages]
  );

  return (
    <Box className={classes.container}>
      <Box className={classes.info}>
        <Typography variant="body2">
          {strings.formatString(strings.resumeSearch.pagination.showing, {
            startItem,
            endItem,
            totalItems
          })}
        </Typography>

        <Box className={classes.itemsPerPageContainer}>
          <Typography variant="body2">{strings.resumeSearch.pagination.showLabel}&nbsp;</Typography>
          <FormControl size="small">
            <Select value={itemsPerPage} onChange={handleItemsPerPageChange}>
              {itemsPerPageOptions.map(option => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Typography variant="body2">{strings.resumeSearch.pagination.perPageLabel}</Typography>
        </Box>
      </Box>

      <Box className={classes.controls}>
        <Tooltip title={paginationCopies.firstPage}>
          <span>
            <Button
              className={classes.pageButton}
              disabled={currentPage === 1}
              onClick={() => handlePageChange(1)}
              size="small"
              variant="outlined"
            >
              <FirstPageIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={paginationCopies.previousPage}>
          <span>
            <Button
              className={classes.pageButton}
              disabled={currentPage === 1}
              onClick={() => handlePageChange(currentPage - 1)}
              size="small"
              variant="outlined"
            >
              <ChevronLeftIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>

        {visiblePages.map(page => (
          <Button
            className={classes.pageButton}
            color={page === currentPage ? 'primary' : 'default'}
            key={`page-${page}`}
            onClick={() => handlePageChange(page)}
            size="small"
            variant={page === currentPage ? 'contained' : 'outlined'}
          >
            {page}
          </Button>
        ))}

        <Tooltip title={paginationCopies.nextPage}>
          <span>
            <Button
              className={classes.pageButton}
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(currentPage + 1)}
              size="small"
              variant="outlined"
            >
              <ChevronRightIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>

        <Tooltip title={paginationCopies.lastPage}>
          <span>
            <Button
              className={classes.pageButton}
              disabled={currentPage === totalPages}
              onClick={() => handlePageChange(totalPages)}
              size="small"
              variant="outlined"
            >
              <LastPageIcon fontSize="small" />
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
};

PaginationComponent.displayName = 'Pagination';
export const Pagination = React.memo(PaginationComponent);
